// Editor từng ô của dòng lọc (filterRow DevExtreme) theo loại cột — dùng chung cho AntGrid và AntDropDownGrid.
// Không gọi e.setValue: DevExtreme không tự lọc (dữ liệu lấy theo trang từ server); giá trị giữ ở nơi gọi và gán lại
// mỗi lần DevExtreme vẽ lại dòng lọc.
//
// prepareColumnFilterEditor(e, { spec, value, onChange(value, immediate), focusState })
//   e          : sự kiện onEditorPreparing (parentType 'filterRow')
//   spec       : kết quả resolveColumnFilterSpec (null → không có ô lọc)
//   value      : giá trị đang lọc của cột
//   onChange   : ghi giá trị; immediate = áp dụng ngay (chọn) / chờ gõ xong (chữ, số)
//   focusState : { current: { field, blurAt } } — ô chữ/số đang gõ lấy lại focus nếu dòng lọc bị vẽ lại

import AntLocalization from './AntLocalization';
import AntTime from './AntTime';

const t = (vi, en) => AntLocalization.translateText({ vi, en });

export const prepareColumnFilterEditor = (e, { spec, value, onChange, focusState }) => {
    if (!spec) {
        e.cancel = true;
        return;
    }
    const field = e.dataField;
    const current = value;
    const inputAttr = (e.editorOptions && e.editorOptions.inputAttr) || {};
    const base = { showClearButton: true, stylingMode: 'outlined', inputAttr, width: '100%' };

    if (spec.kind === 'text' || spec.kind === 'number') {
        e.editorName = 'dxTextBox';
        const active = focusState && focusState.current ? focusState.current : { field: null, blurAt: 0 };
        const restoreFocus = active.field === field && (!active.blurAt || Date.now() - active.blurAt < 1500);
        e.editorOptions = {
            ...base,
            value: current === undefined || current === null ? '' : String(current),
            valueChangeEvent: 'input',
            onFocusIn: () => {
                if (focusState) focusState.current = { field, blurAt: 0 };
            },
            onFocusOut: () => {
                if (focusState && focusState.current.field === field) focusState.current = { field, blurAt: Date.now() };
            },
            onInitialized: restoreFocus
                ? (ev) =>
                      setTimeout(() => {
                          // Chỉ khi focus đang "rơi" (ô cũ vừa bị huỷ), không giành focus người dùng đã chuyển đi.
                          const activeEl = document.activeElement;
                          if (activeEl && activeEl !== document.body) return;
                          const input = ev.element && ev.element.querySelector ? ev.element.querySelector('input') : null;
                          if (!input) return;
                          input.focus();
                          const end = input.value.length;
                          try {
                              input.setSelectionRange(end, end);
                          } catch (err) {
                              // input không hỗ trợ chọn
                          }
                      }, 0)
                : undefined,
            hint:
                spec.kind === 'number'
                    ? t(
                          'Gõ số để lọc số chứa dãy đó (675 → 1.675.000); =1675000 khớp đúng; hoặc >, >=, <, <=, <>',
                          'Type digits to match numbers containing them (675 → 1,675,000); =1675000 for exact; or >, >=, <, <=, <>'
                      )
                    : t('Chứa…', 'Contains…'),
            inputAttr: spec.kind === 'number' ? { ...inputAttr, style: 'text-align: right' } : inputAttr,
            onValueChanged: (ev) => {
                if (ev.event) onChange(ev.value, false);
            },
            onEnterKey: (ev) => onChange(ev.component.option('value'), true)
        };
        return;
    }
    if (spec.kind === 'date') {
        e.editorName = 'dxDateBox';
        e.editorOptions = {
            ...base,
            type: 'date',
            pickerType: 'calendar',
            displayFormat: 'dd/MM/yyyy',
            useMaskBehavior: true,
            value: typeof current === 'number' ? AntTime.fromUnix(current).toDate() : null,
            onValueChanged: (ev) => onChange(ev.value instanceof Date ? AntTime.startOfDay(ev.value).unix() : null, true)
        };
        return;
    }
    if (spec.kind === 'bool') {
        e.editorName = 'dxSelectBox';
        e.editorOptions = {
            ...base,
            dataSource: [
                { value: 1, text: t('Có', 'Yes') },
                { value: 0, text: t('Không', 'No') }
            ],
            valueExpr: 'value',
            displayExpr: 'text',
            placeholder: '',
            value: current === 1 || current === 0 ? current : null,
            onValueChanged: (ev) => onChange(ev.value === 1 || ev.value === 0 ? ev.value : null, true)
        };
        return;
    }
    if (spec.kind === 'values' || spec.kind === 'tags') {
        const values = Array.isArray(current) ? current : [];
        e.editorName = 'dxTagBox';
        e.editorOptions = {
            ...base,
            placeholder: '',
            multiline: false,
            maxDisplayedTags: 1,
            showMultiTagOnly: false,
            value: values,
            onValueChanged: (ev) => onChange(ev.value, true),
            dropDownOptions: { minWidth: 220 },
            ...(spec.kind === 'values'
                ? {
                      dataSource: spec.items,
                      valueExpr: 'value',
                      displayExpr: 'text',
                      searchEnabled: true,
                      showSelectionControls: true,
                      applyValueMode: 'useButtons'
                  }
                : {
                      // Field mảng (tags): gõ rồi Enter để thêm giá trị.
                      items: values.slice(),
                      acceptCustomValue: true,
                      openOnFieldClick: false,
                      showDropDownButton: false,
                      hint: t('Gõ rồi Enter để thêm', 'Type then Enter to add')
                  })
        };
    }
};

export default prepareColumnFilterEditor;
